import Vue from 'vue'
import App from './App.vue'
import router from './router'
import { store } from './store'
import vuetify from './plugins/vuetify'
import axios from 'axios'
import { LoaderPlugin } from 'vue-google-login'
import { Multimedia } from '@/model/Multimedia'
import Youtube from '@/service/Youtube'
import VueSocialSharing from 'vue-social-sharing'
import { VueReCaptcha } from 'vue-recaptcha-v3'
import MultimediaComponent from '@/components/Multimedia.vue'
import VueGtm from 'vue-gtm'

Vue.config.productionTip = false

Vue.filter('deletedUser', function (value: string): string {
  if (value.match(/deleted\.[0-9]+/)) {
    return 'User Deleted'
  }
  return value
})

Vue.filter('orderNumber', function (value: number): string {
  switch (value) {
    case 1:
      return '1st'
    case 2:
      return '2nd'
    case 3:
      return '3rd'
  }

  return value.toString() + 'nd'
})

Vue.filter('round', function (value: number): number {
  return Math.round(value * 10) / 10
})

Vue.filter('nl2br', function (str: string): string {
  if (!str) {
    return ''
  }
  const breakTag = '<br>'
  return str.replace(/([^>\r\n]?)(\r\n|\n\r|\r|\n)/g, '$1' + breakTag + '$2')
})

const multimediaSizes = {
  size1: 0,
  size2: 1,
  size3: 2,
  size4: 3,
  size5: 4,
  size6: 5,
} as Record<string, number>

Vue.filter(
  'multimediapath',
  function (
    multimedia: Multimedia | null,
    defaultImage: string,
    sizeKey: string
  ): string {
    if (!multimedia) {
      return defaultImage
    }

    if (multimedia.type == 'video') {
      return Youtube.getThumbnailLink(Youtube.getMovieId(multimedia.url))
    }

    if(multimedia.type === 'file'){
      if(process.env.VUE_APP_FILE_STRATEGY === 'local'){
        return process.env.VUE_APP_TECH_MASTER_EVENT_API_HOST + multimedia.url
      } else {
        return 'https://' + multimedia.url;
      }
    }

    if(multimedia.type === 'link'){
      return multimedia.url
    }

    return multimedia.url
  }
)

Vue.filter(
  'multimedianame',
  function (multimedia: Multimedia | null, defaultType: string): string {
    if (!multimedia) {
      return 'Default ' + defaultType
    }

    return multimedia.alt
  }
)

Vue.filter('imgpath', function (value: string): string {
  return process.env.VUE_APP_TECH_MASTER_EVENT_IMAGES_HOST + value
})

Vue.filter(
  'attribute',
  function (attributes: Record<string, string>[], key: string): string {
    for (let i = 0; i < attributes.length; i++) {
      if (attributes[i].key === key) return attributes[i].value
    }

    return ''
  }
)

Vue.filter('split', function (value: string, index: number): string {
  return value.split(';')[index]
})

Vue.mixin({
  methods: {
    validateAttribute: function (
      attributes: Record<string, string>[],
      key: string,
      requiredValue: boolean
    ): boolean {
      for (let i = 0; i < attributes.length; i++) {
        if (
          attributes[i].key === key &&
          (!requiredValue || attributes[i].value !== '')
        )
          return true
      }

      return false
    },
  },
})

const headers: Record<string, string> = {
  accept: 'application/ld+json',
}

const api = axios.create({
  baseURL: process.env.VUE_APP_TECH_MASTER_EVENT_API_BASE_URL,
  headers: headers,
})

api.interceptors.request.use(function (config: any): any {
  if (store.state.authentication.signedIn) {
    config.headers['Authorization'] =
      'Bearer ' + store.state.authentication.user.token
  }

  return config
})

api.interceptors.response.use(
  function (response) {
    return response
  },
  function (error) {
    if (error.response) {
      if (
        error.response.data.code === 401 &&
        store.state.authentication.signedIn
      ) {
        store.dispatch('authentication/removeJWTSession')
        return Promise.reject(error)
      }
    }

    return Promise.reject(error)
  }
)

Vue.component('Multimedia', MultimediaComponent)

Vue.use({
  install(Vue) {
    Vue.prototype.$api = api
  },
})

Vue.use(LoaderPlugin, {
  'client_id': process.env.VUE_APP_GOOGLE_LOGIN_CLIENT_ID,
  scope: 'https://www.googleapis.com/auth/user.birthday.read',
})

Vue.use(VueSocialSharing)

Vue.use(VueReCaptcha, { siteKey: process.env.VUE_APP_RECAPTCHA_SITEKEY })

if (
  process.env.VUE_APP_GOOGLE_TAG_MANAGER_ID &&
  process.env.VUE_APP_GOOGLE_TAG_MANAGER_ID.length > 0
) {
  Vue.use(
    VueGtm,
    {
      id: process.env.VUE_APP_GOOGLE_TAG_MANAGER_ID,
      vueRouter: router
    }
  )
}

store.dispatch('countries/load', api)

new Vue({
  router,
  vuetify,
  store,
  render: (h) => h(App),
}).$mount('#app')
